import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { NavBar } from './components/shell/NavBar.tsx';
import { Footer } from './components/shell/Footer.tsx';
import { ErrorBoundary } from './components/shell/ErrorBoundary.tsx';
import { AuthGate } from './components/shell/AuthGate.tsx';
import { PublicShell } from './components/shell/PublicShell.tsx';
import { PWAStatus } from './components/shell/PWAStatus.tsx';
import { Toaster } from './components/ui/Toaster.tsx';
import { UploadPage } from './routes/UploadPage.tsx';
import { EventsPage } from './routes/EventsPage.tsx';
import { EventPage } from './routes/EventPage.tsx';
import { TimelinePage } from './routes/TimelinePage.tsx';
import { MapPage } from './routes/MapPage.tsx';
import { PrivacyPage } from './routes/PrivacyPage.tsx';
import { TermsPage } from './routes/TermsPage.tsx';
import { AdminPage } from './routes/AdminPage.tsx';
import { AdminUserPage } from './routes/AdminUserPage.tsx';
import { GOOGLE_CLIENT_ID } from './config/awsConfig.ts';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/** The authenticated app (everything behind sign-in + access control). */
function GatedApp() {
  return (
    <AuthGate>
      <div className="min-h-screen pb-16">
        <NavBar />
        <main className="mx-auto max-w-6xl px-4 py-6">
          <Routes>
            <Route path="/" element={<Navigate to="/events" replace />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:event" element={<EventPage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/users/:email" element={<AdminUserPage />} />
            <Route path="*" element={<Navigate to="/events" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </AuthGate>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              {/* PUBLIC — reachable without signing in (required for Google OAuth
                  verification, which crawls these URLs unauthenticated). */}
              <Route
                path="/privacy"
                element={
                  <PublicShell>
                    <PrivacyPage />
                  </PublicShell>
                }
              />
              <Route
                path="/terms"
                element={
                  <PublicShell>
                    <TermsPage />
                  </PublicShell>
                }
              />
              {/* Everything else goes through the auth gate. */}
              <Route path="/*" element={<GatedApp />} />
            </Routes>
          </BrowserRouter>
          <PWAStatus />
          <Toaster />
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}
